import type { UseQueryResult } from '@tanstack/react-query';
import { ClipboardList, Hand, Zap } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { EmptyState } from '../atoms/EmptyState';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import type { DosingLog } from '../../services/dosingLogService';
import { formatDoseDateTime } from './formatDoseDateTime';
import './DosingLogSection.css';

export interface DosingLogSectionProps {
  logsQuery: Pick<UseQueryResult<DosingLog[]>, 'data' | 'isLoading' | 'isError' | 'refetch'>;
  onLogDose: () => void;
  onEditLog: (log: DosingLog) => void;
  onDeleteLog: (log: DosingLog) => void;
}

/** "automatisch (Dosierpumpe)" vs "manuell" — icon + text, never colour only. */
export function DosingLogSourceBadge({ source }: { source: DosingLog['source'] }) {
  const { t } = useTranslation();
  const isPump = source === 'pump';
  const Icon = isPump ? Zap : Hand;
  return (
    <Badge tone={isPump ? 'info' : 'neutral'} className="dosing-log__source" data-testid="dosing-log-source" data-source={source}>
      <Icon size={14} aria-hidden="true" focusable="false" />
      {isPump ? t('dosing.logList.sourcePump') : t('dosing.logList.sourceManual')}
    </Badge>
  );
}

/** The aquarium's latest logged doses with edit/delete per entry. */
export function DosingLogSection({ logsQuery, onLogDose, onEditLog, onDeleteLog }: DosingLogSectionProps) {
  const { t, i18n } = useTranslation();
  const logs = logsQuery.data ?? [];

  let content;
  if (logsQuery.isLoading) {
    content = (
      <LiveRegion>
        <Text variant="muted">{t('dosing.logList.loading')}</Text>
      </LiveRegion>
    );
  } else if (logsQuery.isError) {
    content = (
      <LoadErrorState
        title={t('dosing.logList.loadErrorTitle')}
        message={t('dosing.logList.loadErrorMessage')}
        titleLevel={3}
        onRetry={() => void logsQuery.refetch()}
      />
    );
  } else if (logs.length === 0) {
    content = (
      <EmptyState
        icon={<ClipboardList size={48} strokeWidth={1.5} />}
        headline={t('dosing.logList.emptyHeadline')}
        description={t('dosing.logList.empty')}
        actionLabel={t('dosing.logList.logButton')}
        actionVariant="secondary"
        onAction={onLogDose}
        headingLevel={3}
      />
    );
  } else {
    content = (
      <ul className="dosing-log__list" data-testid="dosing-log-list">
        {logs.map((log) => {
          const when = formatDoseDateTime(log.logged_at, i18n?.language);
          const entryLabel = t('dosing.logList.entryLabel', { product: log.product_name, time: when });
          return (
            <li key={log.id} className="dosing-log__item">
              <div className="dosing-log__main">
                <Text weight="semibold">
                  {log.product_name} · {log.dose_amount} {log.dose_unit}
                </Text>
                <Text variant="muted" size="sm">
                  <time dateTime={log.logged_at}>{when}</time>
                </Text>
                {log.notes && <Text size="sm">{log.notes}</Text>}
              </div>
              <div className="dosing-log__meta">
                <DosingLogSourceBadge source={log.source} />
                <div className="dosing-log__actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditLog(log)}
                    aria-label={`${t('dosing.logList.edit')}: ${entryLabel}`}
                  >
                    {t('dosing.logList.edit')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteLog(log)}
                    aria-label={`${t('dosing.logList.delete')}: ${entryLabel}`}
                  >
                    {t('dosing.logList.delete')}
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Section
      title={t('dosing.logList.title')}
      description={t('dosing.logList.description')}
      actions={
        logs.length > 0 ? (
          <Button type="button" variant="secondary" size="sm" onClick={onLogDose}>
            {t('dosing.logList.logButton')}
          </Button>
        ) : undefined
      }
    >
      {content}
    </Section>
  );
}
